'use client';

import { c as _c } from "react/compiler-runtime";
import type { MarketId } from '@morpho-org/blue-sdk';
import { Avatar } from '@morpho-org/ui-v6/client';
import { Tag } from '@morpho-org/ui-v6/server';
import { UNKNOWN_SYMBOL } from '@morpho-org/ui-v6/utils';
import { TruncatedTooltip } from '@repo/ui';
import { useWeb3Context } from '@/contexts/Web3Context';
import { getMarketUrl } from '@/helpers/routing';
import type { AutoDeleverageDialogToken } from './useAutoDeleverageDialogData';
import { LinkIcon } from '../../LinkIcon';
interface AutoDeleverageMarketInfosProps {
  marketId: MarketId;
  collateralAsset: AutoDeleverageDialogToken;
  loanAsset: AutoDeleverageDialogToken;
  formattedLltv: string;
}
const AutoDeleverageMarketInfos = t0 => {
  const $ = _c(37);
  const {
    marketId,
    collateralAsset,
    loanAsset,
    formattedLltv
  } = t0;
  const {
    targetChainIdentifier
  } = useWeb3Context();
  let t1;
  if ($[0] !== loanAsset.logoUri) {
    t1 = <Avatar aria-hidden={true} src={loanAsset.logoUri} size="medium" />;
    $[0] = loanAsset.logoUri;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = loanAsset.symbol || UNKNOWN_SYMBOL;
  let t3;
  if ($[2] !== loanAsset.symbol) {
    t3 = ref => <span ref={ref} className="text-title-xs max-w-[160px] min-w-0 truncate md:max-w-none" style={{
      lineHeight: "normal"
    }}>{loanAsset.symbol || UNKNOWN_SYMBOL}</span>;
    $[2] = loanAsset.symbol;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t2 || $[5] !== t3) {
    t4 = <TruncatedTooltip text={t2}>{t3}</TruncatedTooltip>;
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t1 || $[8] !== t4) {
    t5 = <span className="gap-xs inline-flex min-w-0 items-center">{t1}{t4}</span>;
    $[7] = t1;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  let t7;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <span aria-hidden={true} className="bg-muted-foreground h-lg w-0.5 shrink-0 rounded-full" />;
    t7 = <span className="sr-only">backed by</span>;
    $[10] = t6;
    $[11] = t7;
  } else {
    t6 = $[10];
    t7 = $[11];
  }
  let t8;
  if ($[12] !== collateralAsset.logoUri) {
    t8 = <Avatar aria-hidden={true} src={collateralAsset.logoUri} size="medium" />;
    $[12] = collateralAsset.logoUri;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  const t9 = collateralAsset.symbol || UNKNOWN_SYMBOL;
  let t10;
  if ($[14] !== collateralAsset.symbol) {
    t10 = ref_0 => <span ref={ref_0} className="text-title-xs max-w-[160px] min-w-0 truncate md:max-w-none" style={{
      lineHeight: "normal"
    }}>{collateralAsset.symbol || UNKNOWN_SYMBOL}</span>;
    $[14] = collateralAsset.symbol;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[16] !== t10 || $[17] !== t9) {
    t11 = <TruncatedTooltip text={t9}>{t10}</TruncatedTooltip>;
    $[16] = t10;
    $[17] = t9;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = {
      lineHeight: "normal"
    };
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  let t13;
  if ($[20] !== formattedLltv) {
    t13 = <Tag variant="main"><span className="text-3xs text-foreground" style={t12}>{formattedLltv}</span></Tag>;
    $[20] = formattedLltv;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  let t14;
  if ($[22] !== t11 || $[23] !== t13 || $[24] !== t8) {
    t14 = <span className="gap-xs inline-flex min-w-0 items-center">{t8}{t11}{t13}</span>;
    $[22] = t11;
    $[23] = t13;
    $[24] = t8;
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  const t15 = collateralAsset.symbol ?? "";
  const t16 = loanAsset.symbol ?? "";
  let t17;
  if ($[26] !== marketId || $[27] !== t15 || $[28] !== t16 || $[29] !== targetChainIdentifier) {
    t17 = getMarketUrl({
      identifier: targetChainIdentifier,
      marketId,
      collateralSymbol: t15,
      loanSymbol: t16
    });
    $[26] = marketId;
    $[27] = t15;
    $[28] = t16;
    $[29] = targetChainIdentifier;
    $[30] = t17;
  } else {
    t17 = $[30];
  }
  let t18;
  if ($[31] !== t17) {
    t18 = <LinkIcon tooltip="Open market." href={t17} />;
    $[31] = t17;
    $[32] = t18;
  } else {
    t18 = $[32];
  }
  let t19;
  if ($[33] !== t14 || $[34] !== t18 || $[35] !== t5) {
    t19 = <div className="gap-xs flex items-center">{t5}{t6}{t7}{t14}{t18}</div>;
    $[33] = t14;
    $[34] = t18;
    $[35] = t5;
    $[36] = t19;
  } else {
    t19 = $[36];
  }
  return t19;
};
export default AutoDeleverageMarketInfos;